// src/pages/admin/AdminPanel.jsx
import "../../styles/admin/adminPage.css";
import React, { useState, useEffect, useCallback } from 'react';

import DashboardStats from '../../components/admin/DashboardStats';
import ProductForm from '../../components/admin/ProductForm';
import ProductList from '../../components/admin/ProductList';
import UserList from '../../components/admin/UserList';
import CategorieTitreForm from '../../components/admin/CategorieTitreForm';
import VendeurList from "../../components/admin/VendeurList";
import ListeVendeurValides from "../../components/admin/ListeVendeurValide";
import ListeVendeurRefuses from "../../components/admin/ListeVendeurRefuse";
import VendeurDetails from "../../components/admin/VendeurDetails";
import ProduitsEnAttente from "../../components/admin/ProduitsEnAttente";

import { logoutAdmin } from "../../services/admin/adminAuthService";
import { fetchTitresParCategorie } from '../../services/produitAdmin';
import { fetchProduitsValides } from "../../services/admin/adminProduitService";
import { get } from "../../services/api";

import {
  fetchProduits,
  ajouterProduit,
  modifierProduit,
  supprimerProduit,
  fetchUsers,
} from '../../services/adminService';

import { fetchVendeursParStatut } from "../../services/vendeurs/vendeurService";

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('produits');
  const [activeSubTab, setActiveSubTab] = useState('liste');

  const [produits, setProduits] = useState([]);
  const [users, setUsers] = useState([]);
  const [produitsValides, setProduitsValides] = useState([]);
  const [useCustomTitle, setUseCustomTitle] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [titresOptions, setTitresOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedVendeur, setSelectedVendeur] = useState(null);

  const [formData, setFormData] = useState({
    description: '',
    price: '',
    img: null,
    imagePreview: '',
    categorie_id: '',
    titre_id: '',
    stock: '',
  });

  const [stats, setStats] = useState({
    categories: 0,
    produits: 0,
    clients: 0,
    vendeurs: 0,
  });

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch (err) {
      console.error('Erreur lors de la déconnexion :', err);
    } finally {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
  };

 const loadCategories = useCallback(async () => {
  try {
    const res = await get('/categories');
    setCategories(res.data);
  } catch (err) {
    console.error("Erreur chargement catégories :", err);
  }
}, []);

  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const loadProduitsValides = async () => {
      try {
        const data = await fetchProduitsValides();
        setProduitsValides(data);
      } catch (error) {
        console.error('Erreur chargement produits validés :', error);
      }
    };
    if (activeTab === 'produits' && activeSubTab === 'valides') {
      loadProduitsValides();
    }
  }, [activeTab, activeSubTab]);

  useEffect(() => {
    if (activeTab === 'produits') loadProduits();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'produits' && activeSubTab === 'liste') {
      loadProduits();
    }
  }, [activeTab, activeSubTab]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (activeTab === 'utilisateurs') {
      fetchUsers()
        .then(setUsers)
        .catch((e) => console.error('Erreur chargement utilisateurs :', e));
    }
  }, [activeTab]);

  useEffect(() => {
    const loadTitres = async () => {
      if (formData.categorie_id) {
        try {
          const titres = await fetchTitresParCategorie(formData.categorie_id);
          setTitresOptions(titres);
        } catch (error) {
          console.error('Erreur chargement titres dynamiques :', error);
        }
      } else {
        setTitresOptions([]);
      }
    };
    loadTitres();
  }, [formData.categorie_id]);

  const loadProduits = async () => {
    try {
      const data = await fetchProduits();
      setProduits(data);
    } catch (error) {
      console.error('Erreur chargement produits :', error);
    }
  };

 const loadStats = useCallback(async () => {
  try {
    const [catRes, produitsData, clientsData, vendeursValides] = await Promise.all([
      get('/categories'),
      fetchProduits(),
      fetchUsers(),
      fetchVendeursParStatut('valide'),
    ]);

    const categories = catRes.data ?? [];
    const produits = produitsData.json?.() ? await produitsData.json() : produitsData;
    const clients = clientsData ?? [];
    const vendeurs = vendeursValides ?? [];

    setStats({
      categories: categories.length,
      produits: produits.length,
      clients: clients.length,
      vendeurs: vendeurs.length,
    });
  } catch (err) {
    console.error("Erreur chargement statistiques :", err);
  }
}, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'img') {
      const file = files[0];
      const preview = file ? URL.createObjectURL(file) : '';
      setFormData({ ...formData, img: file, imagePreview: preview });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('description', formData.description);
    data.append('price', formData.price);
    data.append('categorie_id', formData.categorie_id);
    data.append('titre_id', formData.titre_id);
    data.append('stock', formData.stock);
    if (formData.img) data.append('img', formData.img);

    try {
      if (editId) {
        await modifierProduit(editId, data);
        setEditId(null);
      } else {
        await ajouterProduit(data);
      }
      setFormData({
        description: '',
        price: '',
        img: null,
        categorie_id: '',
        titre_id: '',
        stock: '',
      });
      setUseCustomTitle(false);
      setCustomTitle('');
      await loadProduits();
      await loadStats();
    } catch (error) {
      console.error('Erreur enregistrement produit :', error);
    }
  };

  const handleEdit = (produit) => {
    setEditId(produit.id);
    setFormData({
      description: produit.description,
      price: produit.price,
      categorie_id: produit.categorie_id || '',
      titre_id: produit.titre_id || '',
      stock: produit.stock || '',
      img: null,
      imagePreview: produit.img || '',
    });
    setActiveSubTab('gestion');
  };

  const handleDelete = async (id) => {
    try {
      await supprimerProduit(id);
      loadProduits();
      await loadStats();
    } catch (error) {
      console.error('Erreur suppression :', error);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <img src="/images/logotantsaha.png" alt="Logo" className="logo-image" />
        <h3>Tsenan'ny Tantsaha</h3>

        <div className="menu-section">
          <div className="menu-title" onClick={() => { setActiveTab('produits'); setActiveSubTab('liste'); }}>
            Produits
          </div>
          {activeTab === 'produits' && (
            <div className="submenu">
              <button onClick={() => setActiveSubTab('categories')}>Gestion Catégories/Titres</button>
              <button onClick={() => setActiveSubTab('gestion')}>Gestion de produit</button>
              <button onClick={() => setActiveSubTab('liste')}>Liste des produits</button>
              <button onClick={() => setActiveSubTab('attente')}>Produits en attente</button>
              <button onClick={() => setActiveSubTab('valides')}>Produits validés</button>
            </div>
          )}
        </div>

        <div className="menu-section">
          <div className="menu-title" onClick={() => { setActiveTab('vendeurs'); setActiveSubTab('attente'); }}>
            Vendeurs
          </div>
          {activeTab === 'vendeurs' && (
            <div className="submenu">
              <button onClick={() => setActiveSubTab('attente')}>Liste des vendeurs en attente</button>
              <button onClick={() => setActiveSubTab('valides')}>Vendeurs validés</button>
              <button onClick={() => setActiveSubTab('refuses')}>Vendeurs refusés</button>
            </div>
          )}
        </div>

        <div className="menu-section">
          <div className="menu-title" onClick={() => setActiveTab('utilisateurs')}>
            Utilisateurs
          </div>
        </div>

        <div className="menu-section">
          <button onClick={handleLogout} className="logout-button">🔒 Déconnexion</button>
        </div>
      </div>

      <div className="admin-content">
        <div className="dashboard-top">
          <DashboardStats stats={stats} />
        </div>

        <div className="dashboard-main">
          {activeTab === 'produits' && activeSubTab === 'gestion' && (
            <div className="centered-panel">
              <ProductForm
                categories={categories}
                formData={formData}
                titresOptions={titresOptions}
                useCustomTitle={useCustomTitle}
                customTitle={customTitle}
                handleChange={handleChange}
                setUseCustomTitle={setUseCustomTitle}
                setCustomTitle={setCustomTitle}
                handleSubmit={handleSubmit}
                fetchTitresParCategorie={fetchTitresParCategorie}
                setFormData={setFormData}
              />
            </div>
          )}

          {activeTab === 'produits' && activeSubTab === 'valides' && (
            <ProductList
              produits={produitsValides}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          )}

          {activeTab === 'produits' && activeSubTab === 'liste' && (
            <ProductList
              produits={produits}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
            />
          )}

          {activeTab === 'produits' && activeSubTab === 'categories' && (
            <CategorieTitreForm onUpdate={loadCategories} />
          )}

          {activeTab === 'produits' && activeSubTab === 'attente' && <ProduitsEnAttente />}

          {activeTab === 'vendeurs' && activeSubTab === 'attente' && !selectedVendeur && (
            <VendeurList onSelect={setSelectedVendeur} />
          )}

          {activeTab === 'vendeurs' && activeSubTab === 'attente' && selectedVendeur && (
            <VendeurDetails vendeur={selectedVendeur} onBack={() => setSelectedVendeur(null)} />
          )}

          {activeTab === 'vendeurs' && activeSubTab === 'valides' && <ListeVendeurValides />}

          {activeTab === 'vendeurs' && activeSubTab === 'refuses' && <ListeVendeurRefuses />}

          {activeTab === 'utilisateurs' && <UserList users={users} />}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
